"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { BackendInputWithLabel } from "@/app/components/backend";
import { LoginButton } from "./login-button";
import PasswordInput from "./password-input";
import { ErrorMessage } from "./error-message";
import { signin } from "@/app/services/auth/signin";
import { AuthErrorContainer } from "./auth-error-container";
import { LinkAnchor } from "@/app/components/ui/link-anchor";

// Typ für das Rückgabeobjekt von signin
interface SigninResult {
  success?: boolean;
  errors?: {
    emailError?: string;
    passwordError?: string;
    authError?: string;
    connectionError?: string;
  };
}

type LoginFormProps = {
  emailError?: string;
  passwordError?: string;
  authError?: string;
  connectionError?: string;
};

export default function LoginForm({
  emailError: initialEmailError,
  passwordError: initialPasswordError,
  authError: initialAuthError,
  connectionError: initialConnectionError,
}: LoginFormProps) {
  const router = useRouter();
  const [, startTransition] = useTransition();
  const [emailError, setEmailError] = useState(initialEmailError || "");
  const [passwordError, setPasswordError] = useState(
    initialPasswordError || ""
  );
  const [authError, setAuthError] = useState(initialAuthError || "");
  const [connectionError, setConnectionError] = useState(
    initialConnectionError || ""
  );

  async function handleSubmit(formData: FormData) {
    setEmailError("");
    setPasswordError("");
    setAuthError("");
    setConnectionError("");
    startTransition(async () => {
      try {
        const result = (await signin(formData)) as SigninResult;
        if (result?.success) {
          // Login landet vorerst wieder im Dashboard; wer kein backend.access
          // hat, wird vom Dashboard-Layout ins Intranet weitergereicht.
          router.push("/dashboard");
        } else if (result?.errors) {
          setEmailError(result.errors.emailError || "");
          setPasswordError(result.errors.passwordError || "");
          setAuthError(result.errors.authError || "");
          setConnectionError(result.errors.connectionError || "");
        } else {
          setAuthError("Unbekannter Fehler beim Login");
        }
      } catch (e) {
        console.error(e);
        setConnectionError("Verbindungsfehler beim Login");
      }
    });
  }

  return (
    <form action={handleSubmit}>
      {/* Input-Gruppe */}
      <div className="flex flex-col gap-3 sm:gap-6">
        {/* Email */}
        <div className="relative">
          <BackendInputWithLabel
            name="email"
            autoComplete="username"
            label="E-Mail-Adresse"
            aria-label="E-Mail-Adresse"
            type="email"
            id="email"
            aria-invalid={!!emailError}
            className="h-12"
          />
          <ErrorMessage message={emailError} />
        </div>
        {/* Password */}
        <div className="flex flex-col gap-1.5">
          <PasswordInput
            passwordError={passwordError}
            showPasswordToggle
            className="h-12"
            autoComplete="current-password"
          />
          <div className="flex justify-end">
            <LinkAnchor
              className="text-[var(--c-accent)] hover:underline"
              href="/request-password"
            >
              Passwort vergessen
            </LinkAnchor>
          </div>
        </div>
      </div>
      {/* Fehler & Button */}
      <div className="mt-4">
        {connectionError && (
          <AuthErrorContainer error={connectionError} className="mb-3" />
        )}
        {authError && !connectionError && (
          <AuthErrorContainer error={authError} className="mb-3" />
        )}

        <LoginButton />

        {/* Static on purpose: the login errors stay deliberately generic, so
            this must not depend on whether the account exists or is confirmed. */}
        <p className="text-muted-foreground mt-4 text-center text-sm">
          E-Mail-Adresse noch nicht bestätigt?
          <LinkAnchor
            href="/registrieren/erneut-senden"
            className="text-[var(--c-accent)] ml-1 hover:underline"
          >
            Bestätigungslink erneut anfordern
          </LinkAnchor>
        </p>
      </div>
    </form>
  );
}
